import { Injectable } from '@angular/core'
import * as AuthActions from '@auth/actions/auth.actions'
import { AuthService } from '@auth/services/auth.service'
import { Store } from '@ngrx/store'
import {
  LOGIN_REDIRECT_COUNT_KEY,
  MAX_LOGIN_REDIRECTS
} from '../auth/auth-keys'

/**
 * Anmeldeportal. Mit `?returnTo=…` springt es nach erfolgreicher Anmeldung
 * direkt in das aufrufende Werkzeug zurück, statt den Benutzer auf dem
 * Portal-Dashboard stehen zu lassen.
 *
 * Wurzeladresse und nicht `/login`: Das Portal liest `returnTo` bereits im
 * Konstruktor seiner Wurzelkomponente, also vor dem Routing.
 */
export const LOGIN_URL = 'https://login.muessig.app/'

/** Anmeldeparameter, die niemals Teil eines Rücksprungziels sein dürfen. */
const AUTH_PARAMS = ['user', 'token', 'tstoken']

const BRAND = '#143d8c'

/**
 * Die EINZIGE Stelle, die abmeldet.
 *
 * Benutzt von
 *  - `TimeoutInterceptor` (Backend antwortet 401/403/"wrong credentials"),
 *  - `AuthGuard` (kein oder abgelaufener Token beim Seitenwechsel),
 *  - `LoginPageComponent` (Aufruf ohne Sitzung),
 *  - `AppComponent.logout()` (Abmelden von Hand).
 *
 * Reihenfolge ist bedeutungstragend: erst der Zustand im Speicher, dann alle
 * Artefakte im `localStorage`, ZULETZT der Sprung. Umgekehrt läuft das
 * Aufräumen gegen den Seitenwechsel und verliert.
 */
@Injectable({
  providedIn: 'root'
})
export class SecurityService {
  /**
   * Schlagen zehn Aufrufe gleichzeitig mit 401 fehl, darf genau EIN Sprung
   * herauskommen. Ohne diese Sperre entsteht eine Abmelde-Schleife.
   */
  private isLoggingOut = false

  constructor(private authService: AuthService, private store: Store) {}

  /**
   * Harte Abmeldung mit Rücksprung.
   *
   * @param reason Was dem Benutzer erklärt wird, falls der Sprung nicht möglich ist.
   * @param returnTo Ziel nach der Anmeldung. Leer lassen für die aktuelle
   *   Seite, `null` für "kein Rücksprung" (Abmelden von Hand).
   */
  forceLogout(
    reason = 'Ihre Sitzung ist abgelaufen.',
    returnTo?: string | null
  ): void {
    if (this.isLoggingOut) {
      return
    }
    this.isLoggingOut = true

    this.clearSession()
    this.leaveToLogin(reason, returnTo)
  }

  /**
   * Weiterleitung zur Anmeldung ohne Abmelde-Meldung.
   *
   * Für den Fall "es gab noch keine Sitzung" - etwa beim direkten Aufruf einer
   * Adresse ohne Token. Räumt trotzdem auf: Ein halber Speicherstand ist
   * schlimmer als keiner.
   */
  redirectToLogin(returnTo?: string | null): void {
    if (this.isLoggingOut) {
      return
    }
    this.isLoggingOut = true

    this.clearSession()
    this.leaveToLogin('Für dieses Werkzeug ist eine Anmeldung nötig.', returnTo)
  }

  /**
   * Bestätigt eine tragende Sitzung und setzt den Schleifenzähler zurück.
   *
   * Wird aufgerufen, sobald der Benutzer aufgelöst ist. Damit gilt das
   * Weiterleitungsbudget immer nur für den gerade laufenden Anmeldeversuch und
   * nicht für einen späteren, echten Sitzungsablauf.
   */
  confirmSession(): void {
    try {
      sessionStorage.removeItem(LOGIN_REDIRECT_COUNT_KEY)
    } catch {
      /* Speicher gesperrt - dann greift eben das Budget. */
    }
  }

  /**
   * Rücksprungziel: aktuelle Seite samt fachlicher Suchparameter, aber ohne
   * die Anmeldeparameter.
   *
   * Die Referenzwerkzeuge lassen `window.location.search` weg und verlieren
   * damit fachliche Parameter. Hier bleiben sie erhalten - das Portal hängt
   * `user`, `token` und `tstoken` einfach zusätzlich an, und die Übernahme
   * entfernt nur diese drei wieder.
   */
  buildReturnTo(pathAndQuery?: string): string {
    const origin = window.location.origin
    try {
      const url = new URL(
        pathAndQuery ?? window.location.pathname + window.location.search,
        origin
      )
      AUTH_PARAMS.forEach((name) => url.searchParams.delete(name))
      return url.toString()
    } catch {
      return origin + '/'
    }
  }

  // ===== intern =====

  private clearSession(): void {
    // 1) Zustand im Speicher
    this.store.dispatch(AuthActions.clearAuthUser())
    // 2) alle Artefakte im localStorage
    this.authService.removeAuthUserToken()
    this.authService.removeJWTToken()
    this.authService.removeTsToken()
    this.authService.removeUserParam()
  }

  private leaveToLogin(reason: string, returnTo?: string | null): void {
    if (!this.mayRedirect()) {
      // Die Übergabe greift grundsätzlich nicht. Ein weiterer Sprung wäre nur
      // noch eine Endlosschleife zwischen Werkzeug und Portal.
      this.showDeadEnd(reason)
      return
    }

    const target =
      returnTo === null ? undefined : this.buildReturnTo(returnTo || undefined)

    // `replace` statt `href`: So bleibt kein toter Werkzeug-Eintrag im Verlauf
    // stehen, auf den die Zurück-Schaltfläche wieder hineinläuft.
    window.location.replace(this.buildLoginUrl(target))
  }

  private buildLoginUrl(returnTo?: string): string {
    try {
      const url = new URL(LOGIN_URL)
      if (returnTo) {
        url.searchParams.set('returnTo', returnTo)
      }
      return url.toString()
    } catch {
      return LOGIN_URL
    }
  }

  /** Zählt die Weiterleitung mit und sagt, ob sie noch erlaubt ist. */
  private mayRedirect(): boolean {
    try {
      const used = Number(sessionStorage.getItem(LOGIN_REDIRECT_COUNT_KEY)) || 0
      if (used >= MAX_LOGIN_REDIRECTS) {
        return false
      }
      sessionStorage.setItem(LOGIN_REDIRECT_COUNT_KEY, String(used + 1))
      return true
    } catch {
      // Kein sessionStorage - dann lieber weiterleiten als aussperren.
      return true
    }
  }

  /**
   * Sackgasse: Erklärt die Lage statt weiter zu springen.
   *
   * Absichtlich reines DOM und kein Angular-Baustein. Diese Meldung muss auch
   * dann erscheinen, wenn die Anwendung selbst nicht mehr tragfähig ist.
   */
  private showDeadEnd(reason: string): void {
    const text =
      reason +
      ' Die Anmeldung liess sich nicht übernehmen. Bitte melden Sie sich im Portal neu an.'

    try {
      const existing = document.getElementById('mu-auth-deadend')
      if (existing) {
        return
      }

      const overlay = document.createElement('div')
      overlay.id = 'mu-auth-deadend'
      overlay.setAttribute(
        'style',
        [
          'position:fixed',
          'inset:0',
          'z-index:99999',
          'display:flex',
          'align-items:center',
          'justify-content:center',
          'background:rgba(255,255,255,0.96)',
          'font-family:Roboto,Helvetica,Arial,sans-serif'
        ].join(';')
      )

      const card = document.createElement('div')
      card.setAttribute(
        'style',
        [
          'max-width:420px',
          'padding:28px 32px',
          'border:1px solid #d9dde6',
          'border-top:3px solid ' + BRAND,
          'border-radius:4px',
          'background:#fff',
          'box-shadow:0 8px 28px rgba(20,61,140,0.12)',
          'text-align:center'
        ].join(';')
      )

      const title = document.createElement('div')
      title.textContent = 'Anmeldung nicht möglich'
      title.setAttribute(
        'style',
        'font-size:18px;font-weight:600;color:' + BRAND + ';margin-bottom:10px'
      )

      const body = document.createElement('div')
      body.textContent = text
      body.setAttribute(
        'style',
        'font-size:14px;line-height:1.5;color:#3c4257;margin-bottom:22px'
      )

      const button = document.createElement('button')
      button.type = 'button'
      button.textContent = 'Zum Portal'
      button.setAttribute(
        'style',
        [
          'appearance:none',
          'border:0',
          'border-radius:3px',
          'padding:9px 20px',
          'font-size:14px',
          'cursor:pointer',
          'color:#fff',
          'background:' + BRAND
        ].join(';')
      )
      button.addEventListener('click', () => {
        // Von Hand ausgelöst: Budget zurücksetzen, damit der Versuch zählt.
        this.confirmSession()
        window.location.replace(LOGIN_URL)
      })

      card.appendChild(title)
      card.appendChild(body)
      card.appendChild(button)
      overlay.appendChild(card)
      document.body.appendChild(overlay)
    } catch {
      // Kein darstellbares DOM - dann wenigstens der harte Hinweis.
      window.alert(text)
    }
  }
}
